import React, { useEffect, useRef, useState } from "react";
import { Modal, Form, Input, Button, Select } from "antd";
import { useSearchParams } from "react-router-dom";
import { svg } from "../../../../Assets/svg";
import { renderFormLabel } from "../../../../Components/Components";
import { useAppDispatch } from "../../../../store";
import GoogleAddressAutocomplete, {
  GoogleAddressResult,
  EUROPE_BOUNDS,
} from "../../../../Components/GoogleAddressAutocomplete";
import {
  createClientBillingAddressAPI,
  updateClientBillingAddressAPI,
  getSageCountriesAPI,
} from "../clientDistributorsApis";

interface Props {
  open: boolean;
  onClose: () => void;
  clientId?: string | null;
  refreshData?: (newAddress?: any) => void;
  addressData?: any;
}

export const ModifyBillingAddressModal: React.FC<Props> = ({
  open,
  onClose,
  clientId,
  refreshData,
  addressData,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const orderId = searchParams.get("order_id");
  const contextualParams = quoteId
    ? { quote_id: quoteId }
    : orderId
    ? { order_id: orderId }
    : undefined;
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [sageCountries, setSageCountries] = useState<any[]>([]);
  const [countriesLoading, setCountriesLoading] = useState<boolean>(false);
  const sageCountriesRef = useRef<any[]>([]);
  sageCountriesRef.current = sageCountries;

  // State to hold coordinates (hidden from UI)
  const [coordinates, setCoordinates] = useState<{
    lat: number | null;
    long: number | null;
  }>({ lat: null, long: null });

  // Fetch Sage countries when modal opens
  useEffect(() => {
    if (open) {
      setCountriesLoading(true);
      dispatch(
        getSageCountriesAPI({
          onSuccess: (data: any) => {
            const list = Array.isArray(data) ? data : data?.list || [];
            setSageCountries(list);
            sageCountriesRef.current = list;
            setCountriesLoading(false);
          },
          onError: () => {
            setCountriesLoading(false);
          },
        }),
      );
    }
  }, [open, dispatch]);

  // Load existing data if available
  useEffect(() => {
    if (open) {
      if (addressData) {
        let countryValue =
          typeof addressData.country === "object" &&
          addressData.country !== null
            ? addressData.country._id
            : addressData.country;

        // If countryValue is a string name or code, attempt to match to country _id
        if (countryValue && sageCountries.length > 0) {
          const match = sageCountries.find(
            (c: any) =>
              c._id === countryValue ||
              c.name?.toLowerCase() === String(countryValue)?.toLowerCase() ||
              c.code?.toLowerCase() === String(countryValue)?.toLowerCase(),
          );
          if (match) {
            countryValue = match._id;
          }
        }

        form.setFieldsValue({
          streetAddress: addressData.address,
          googleLocation: addressData.address,
          country: countryValue || undefined,
          state: addressData.state,
          city: addressData.city,
          zip: addressData.zipcode,
        });
        // Pre-fill coordinates if editing existing data
        setCoordinates({
          lat: addressData.lat || null,
          long: addressData.lng || null,
        });
      } else {
        form.resetFields();
        setCoordinates({ lat: null, long: null });
      }
    }
  }, [open, addressData, form, sageCountries]);

  // Handle Google Autocomplete Selection
  const handlePlaceSelect = (place: GoogleAddressResult) => {
    setCoordinates({
      lat: place.lat,
      long: place.lng,
    });

    const countries =
      sageCountriesRef.current.length > 0
        ? sageCountriesRef.current
        : sageCountries;

    let matchedCountryId = form.getFieldValue("country");
    if (place.country && countries.length > 0) {
      const gCountry = place.country.trim().toLowerCase();
      const match = countries.find(
        (c: any) =>
          c.name?.trim().toLowerCase() === gCountry ||
          c.code?.trim().toLowerCase() === gCountry ||
          ((gCountry.includes("kingdom") || gCountry.includes("britain")) &&
            (c.name?.toLowerCase().includes("britain") || c.code === "GB")) ||
          (gCountry.includes("ireland") &&
            (c.name?.toLowerCase().includes("ireland") || c.code === "IE")),
      );
      if (match) {
        matchedCountryId = match._id;
      }
    }

    form.setFieldsValue({
      streetAddress: place.address || "",
      country: matchedCountryId || undefined,
      state: place.state || "",
      city: place.city || "",
      zip: place.zip || "",
    });
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Construct base payload
      const payload: any = {
        customer_id: clientId, // Can be null
        address: values.streetAddress,
        country: values.country,
        state: values.state,
        city: values.city,
        zipcode: values.zip,
        lat: coordinates.lat,
        lng: coordinates.long,
      };

      // Check if we have a real Client ID
      if (clientId) {
        // --- EXISTING CLIENT: CALL API ---
        const commonCallbacks = {
          onSuccess: (data: any) => {
            setSaving(false);
            onClose();
            refreshData?.(data);
          },
          onError: () => {
            setSaving(false);
          },
        };

        if (addressData) {
          // --- UPDATE MODE ---
          payload.id = addressData._id;
          dispatch(
            updateClientBillingAddressAPI({
              payload: payload,
              params: contextualParams,
              ...commonCallbacks,
            }),
          );
        } else {
          // --- CREATE MODE ---
          dispatch(
            createClientBillingAddressAPI({
              payload: payload,
              params: contextualParams,
              ...commonCallbacks,
            }),
          );
        }
      } else {
        // --- NEW CLIENT (LOCAL): RETURN OBJECT IMMEDIATELY ---
        const selectedCountry = sageCountries.find(
          (c: any) => c._id === values.country,
        );

        // 1. Create a temporary ID so the frontend can track/select it
        const tempAddress = {
          ...payload,
          country: selectedCountry,
          country_details: selectedCountry || null,
          _id: addressData?._id || `temp_${Date.now()}`, // Keep existing temp ID if editing
          isLocal: true,
        };

        // 2. Simulate delay and return object
        setTimeout(() => {
          setSaving(false);
          refreshData?.(tempAddress);
          onClose();
        }, 300);
      }
    } catch (error) {
      console.log("Validation Failed:", error);
      setSaving(false);
    }
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={svg?.closeIcon}
      width={"35rem"}
      className="commonModalClass"
      wrapClassName="zn-1051"
      title={
        <div className="text-center fs-4 fw-bolder lh-m">
          {addressData ? "Update Billing Address" : "Add Billing Address"}
        </div>
      }
    >
      <Form
        layout="vertical"
        form={form}
        className="page-form"
        onFinish={onSave}
        autoComplete="off"
      >
        {/* 1. Google Location Search */}
        <Form.Item
          label={renderFormLabel("Google Location", false)}
          name="googleLocation"
          required={false}
        >
          <GoogleAddressAutocomplete
            placeholder="Search Google Location"
            onPlaceSelect={handlePlaceSelect}
            countryCode={[]}
            bounds={EUROPE_BOUNDS}
            strictBounds={true}
          />
        </Form.Item>

        {/* 2. Street Address (Editable) */}
        <Form.Item
          label={renderFormLabel("Street Address", false)}
          name="streetAddress"
          rules={[
            { required: true, message: "Street Address is required" },
            { whitespace: true, message: "Cannot be empty" },
          ]}
          required={false}
        >
          <Input
            placeholder="Enter Address"
            className="form-control form-control-lg"
          />
        </Form.Item>

        {/* 3. Two Column Layout for Fields */}
        <div className="row gy-4">
          {/* Country */}
          <div className="col-md-6" >
            <Form.Item
              label={renderFormLabel("Country", false)}
              name="country"
              required={false}
              rules={[{ required: true, message: "Country is required" }]}
            >
              <Select
                showSearch
                placeholder="Select Country"
                loading={countriesLoading}
                popupClassName="full-text-select-dropdown"
                suffixIcon={svg?.selectDownArrow}
                filterOption={(input, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={sageCountries?.map((item: any) => ({
                  ...item,
                  value: item?._id,
                  label: item?.name,
                }))}
                allowClear
              />
            </Form.Item>
          </div>

          {/* State */}
          <div className="col-md-6">
            <Form.Item
              label={renderFormLabel("State", false)}
              name="state"
              required={false}
              rules={[
                { required: true, message: "State is required" },
                { whitespace: true, message: "Cannot be empty" },
              ]}
            >
              <Input
                placeholder="State"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>
          {/* City */}
          <div className="col-md-6">
            <Form.Item
              label={renderFormLabel("City", false)}
              name="city"
              required={false}
              rules={[
                { required: true, message: "City is required" },
                { whitespace: true, message: "Cannot be empty" },
              ]}
            >
              <Input
                placeholder="City"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>

          {/* Postal Code */}
          <div className="col-md-6">
            <Form.Item
              label={renderFormLabel("Postal Code", false)}
              name="zip"
              required={false}
              rules={[
                { required: true, message: "Postal Code is required" },
                { whitespace: true, message: "Cannot be empty" },
              ]}
            >
              <Input
                placeholder="Postal Code"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>
        </div>

        {/* Buttons */}
        <div className="d-flex justify-content-center gap-3 mt-4">
          <Button
            onClick={onClose}
            className="btn btn-lg btn-outline-secondary h-auto px-5"
            type="default"
          >
            CLOSE
          </Button>

          <Button
            onClick={onSave}
            loading={saving}
            className="btn btn-lg btn-dark h-auto px-5"
          >
            {addressData ? "UPDATE" : "SAVE"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
